import { SHARING_MODE, type SharingMode } from './types';

/**
 * Interface for objects containing scheduling information used in flavor filtering.
 * Uses flexible string types to accommodate both strict enums and GraphQL string types.
 */
export type HasSchedulingInfo = {
  scheduling: {
    language: string;
    marketplace: string;
    platform: string;
  };
};

/**
 * Base interface for flavor-like objects that can be filtered and matched.
 * Extends scheduling info with active state for filtering purposes.
 *
 * The `active` field is used by functions in this module which automatically
 * filter to only include active flavors in their results.
 */
export type FlavorLike = HasSchedulingInfo & {
  active: boolean;
};

const languageMatcher = <T extends HasSchedulingInfo>({ flavor1, flavor2 }: { flavor1: T; flavor2: T }) =>
  flavor1.scheduling.language.split('_')[0] === flavor2.scheduling.language.split('_')[0];
const marketplaceMatcher = <T extends HasSchedulingInfo>({ flavor1, flavor2 }: { flavor1: T; flavor2: T }) =>
  flavor1.scheduling.marketplace === flavor2.scheduling.marketplace;
const platformMatcher = <T extends HasSchedulingInfo>({ flavor1, flavor2 }: { flavor1: T; flavor2: T }) =>
  flavor1.scheduling.platform === flavor2.scheduling.platform;

/**
 * Mapping of sharing modes to their respective matching logic.
 * Each matcher compares scheduling properties between flavors to determine compatibility.
 * All comparisons are symmetric - order of flavors doesn't matter.
 */
const MATCHERS: Readonly<Record<SharingMode, <T extends HasSchedulingInfo>({ flavor1, flavor2 }: { flavor1: T; flavor2: T }) => boolean>> =
  {
    [SHARING_MODE.ALL]: () => true,
    [SHARING_MODE.LANGUAGE]: languageMatcher,
    [SHARING_MODE.LOCALE]: (flavors) => marketplaceMatcher(flavors) && languageMatcher(flavors),
    [SHARING_MODE.MARKETPLACE]: marketplaceMatcher,
    [SHARING_MODE.NONE]: () => false,
    [SHARING_MODE.PLATFORM]: platformMatcher,
    [SHARING_MODE.PLATFORM_AND_LANGUAGE]: (flavors) => platformMatcher(flavors) && languageMatcher(flavors),
    [SHARING_MODE.PLATFORM_AND_MARKETPLACE]: (flavors) => platformMatcher(flavors) && marketplaceMatcher(flavors),
    [SHARING_MODE.STRUCTURE]: () => true, // Structure sharing treats all flavors as matches during content cloning
  } as const;

/**
 * Determines if two flavors are compatible based on the specified sharing mode.
 * Uses the appropriate matcher function to compare scheduling properties.
 * Comparison is symmetric - order of flavors doesn't matter.
 */
export const flavorsMatch = <T extends HasSchedulingInfo>({
  sharingMode,
  flavor1,
  flavor2,
}: {
  sharingMode: SharingMode;
  flavor1: T;
  flavor2: T;
}) =>
  !!MATCHERS[sharingMode]({
    flavor1,
    flavor2,
  });

/**
 * Determines if a flavor should be included as a sharing target based on context.
 * Excludes the current flavor and inactive flavors.
 */
const shouldIncludeFlavor = <T extends FlavorLike>({
  flavor,
  index,
  currentFlavorIndex,
  currentFlavor,
  sharingMode,
}: {
  flavor: T;
  index: number;
  currentFlavorIndex: number;
  currentFlavor: HasSchedulingInfo;
  sharingMode: SharingMode;
}) =>
  index !== currentFlavorIndex &&
  flavor.active &&
  flavorsMatch({
    flavor1: currentFlavor,
    flavor2: flavor,
    sharingMode,
  });

/**
 * Gets indexes of flavors that match the sharing criteria for an existing flavor being edited.
 *
 * **Active Filtering**: This function automatically filters out inactive flavors and the current flavor.
 * It's designed for variable sharing scenarios where you need to update other active flavors
 * when editing an existing flavor.
 *
 * @param params - Configuration object containing sharing mode, current flavor info, and all flavors
 * @param params.sharingMode - The sharing mode to match against
 * @param params.currentFlavorIndex - Index of the flavor being edited (will be excluded from results)
 * @param params.currentFlavor - The flavor being edited (provides matching criteria)
 * @param params.allFlavors - Array of all flavors to search through (will be filtered to active only)
 * @returns Array of matching flavor indexes (excludes current flavor and inactive flavors)
 */
export const getTargetActiveFlavorIndexes = <T extends FlavorLike>({
  allFlavors,
  sharingMode,
  currentFlavorIndex,
  currentFlavor,
}: {
  allFlavors: T[];
  sharingMode: SharingMode;
  currentFlavorIndex: number;
  currentFlavor: HasSchedulingInfo;
}): number[] =>
  allFlavors
    .map((flavor, index) => ({
      flavor,
      index,
    }))
    .filter(({ flavor, index }) =>
      shouldIncludeFlavor({
        currentFlavor,
        currentFlavorIndex,
        flavor,
        index,
        sharingMode,
      }),
    )
    .map(({ index }) => index);
